'use client';

import { Maximize2, Minimize2, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { ImageCanvas } from '@kit/ui/image-viewer';
import { cn } from '@kit/ui/utils';

interface ImageViewerProps {
  url: string;
  fileName: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onError: () => void;
}

/**
 * Image viewer with zoom + fullscreen controls. The zoom/pan/render body is
 * the shared `@kit/ui` {@link ImageCanvas}; this component owns only the
 * document-viewer control bar and is driven by `DocumentViewerDialog`.
 */
export function ImageViewer({
  url,
  fileName,
  zoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  isFullscreen,
  onToggleFullscreen,
  onError,
}: ImageViewerProps) {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col">
      {/* Controls */}
      <div className="bg-muted/30 flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onZoomOut}
            disabled={zoom <= 50}
            aria-label={t('documents:image.zoomOut')}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="min-w-[60px] text-center text-sm font-medium">
            {zoom}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={onZoomIn}
            disabled={zoom >= 200}
            aria-label={t('documents:image.zoomIn')}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          {zoom !== 100 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetZoom}
              aria-label={t('documents:image.resetZoom')}
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleFullscreen}
          aria-label={
            isFullscreen
              ? t('documents:image.exitFullscreen')
              : t('documents:image.fullscreen')
          }
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4" />
          ) : (
            <Maximize2 className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Image — shared zoom/pan canvas */}
      <ImageCanvas
        src={url}
        alt={fileName}
        zoom={zoom}
        onError={onError}
        className={cn(
          'bg-muted/10 p-4',
          isFullscreen && 'bg-background fixed inset-0 z-50',
        )}
      />
    </div>
  );
}
